'use client'
import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface ReCaptchaContextType {
    executeRecaptcha: (action: string) => Promise<string>
    loaded: boolean
}

const ReCaptchaContext = createContext<ReCaptchaContextType | null>(null)

export const useLazyReCaptcha = (): {
    executeRecaptcha?: (action: string) => Promise<string>
    loaded: boolean
} => {
    const context = useContext(ReCaptchaContext)
    if (!context) {
        return { executeRecaptcha: undefined, loaded: false }
    }
    return context
}

interface LazyReCaptchaProviderProps {
    siteKey: string
    children: ReactNode
}

export default function LazyReCaptchaProvider({
    siteKey,
    children,
}: LazyReCaptchaProviderProps) {
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        if (!siteKey || siteKey.trim() === '' || typeof window === 'undefined' || loaded) return

        // Проверяем, не загружен ли уже скрипт
        if (window.grecaptcha) {
            window.grecaptcha.ready(() => {
                setLoaded(true)
            })
            return
        }

        // Проверяем, не загружается ли уже скрипт
        const existingScript = document.querySelector(`script[src*="recaptcha/api.js"]`)
        if (existingScript) {
            // Скрипт уже загружается, ждем его загрузки
            const checkInterval = setInterval(() => {
                if (window.grecaptcha) {
                    window.grecaptcha.ready(() => {
                        setLoaded(true)
                        clearInterval(checkInterval)
                    })
                }
            }, 100)
            
            // Таймаут на случай если скрипт не загрузится
            const timeout = setTimeout(() => {
                clearInterval(checkInterval)
            }, 10000)
            
            return () => {
                clearInterval(checkInterval)
                clearTimeout(timeout)
            }
        }

        // Загружаем скрипт reCAPTCHA сразу при монтировании
        const script = document.createElement('script')
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
        script.async = true
        script.defer = true
        script.onload = () => {
            // Даем время для инициализации grecaptcha
            const initCheck = () => {
                if (window.grecaptcha) {
                    window.grecaptcha.ready(() => {
                        setLoaded(true)
                    })
                } else {
                    // Если еще не готов, проверяем через 100ms
                    setTimeout(initCheck, 100)
                }
            }
            initCheck()
        }
        script.onerror = () => {
            console.error('Failed to load reCAPTCHA script')
        }
        document.head.appendChild(script)

        return () => {
            // Очистка при размонтировании не требуется, скрипт остается в DOM
        }
    }, [siteKey, loaded])

    const executeRecaptcha = async (action: string): Promise<string> => {
        // Проверяем напрямую window.grecaptcha
        if (typeof window === 'undefined') {
            throw new Error('reCAPTCHA not available (server-side)')
        }

        // Ждем загрузки (максимум 5 секунд)
        let attempts = 0
        const maxAttempts = 25 // 25 * 200ms = 5 секунд
        while (!window.grecaptcha && attempts < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, 200))
            attempts++
        }
        
        if (!window.grecaptcha) {
            throw new Error('reCAPTCHA not loaded. Please refresh the page.')
        }

        const grecaptcha = window.grecaptcha

        // Обновляем состояние loaded, если оно еще false
        if (!loaded) {
            setLoaded(true)
        }

        return new Promise((resolve, reject) => {
            try {
                grecaptcha.ready(() => {
                    grecaptcha
                        .execute(siteKey, { action })
                        .then((token: string) => {
                            resolve(token)
                        })
                        .catch((error: unknown) => {
                            reject(error)
                        })
                })
            } catch (error) {
                reject(error)
            }
        })
    }

    const value: ReCaptchaContextType = {
        executeRecaptcha,
        loaded,
    }

    return <ReCaptchaContext.Provider value={value}>{children}</ReCaptchaContext.Provider>
}

// Типы для window.grecaptcha
declare global {
    interface Window {
        grecaptcha?: {
            ready: (callback: () => void) => void
            execute: (siteKey: string, options: { action: string }) => Promise<string>
        }
    }
}

